/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useState} from 'react';
import {ActivateHandle} from "../../../util/interfaces";
import {Rating} from "../../../util/types";
import {exportRatings} from "../../../util/requestSender";
import {nullify, ResponseData, showDialog} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {createErrorMessage} from "../../../util/utils";
import CreateRatingExport from "./createRatingExport";
import {AppContext} from "../../misc/appContext";

interface Props {
    onReload: VoidFunction;
    ean?: string;
}

function CreateRatingExportController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {onReload, ean} = props;
    const [fromDate, setFromDate] = useState<IDate | null>(null);
    const [toDate, setToDate] = useState<IDate | null>(null);
    const [onlyRatings, setOnlyRatings] = useState<Set<Rating>>(new Set([1, 2, 3, 4, 5]));
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(false);
    const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setFromDate(null);
        setToDate(null);
        setOnlyRatings(new Set([1, 2, 3, 4, 5]));
        if(account) setSelectedAccounts(new Set([account.id]));
        else setSelectedAccounts(new Set());
    }

    function onSelectAccounts(accountIDs: Array<string>) {
        setSelectedAccounts(new Set(accountIDs));
    }

    function onChangeFromDate(value: IDate) {
        setFromDate(value);
    }

    function onChangeToDate(value: IDate) {
        setToDate(value);
    }

    function onToggleAllRatings() {
        const allSelected = ![1, 2, 3, 4, 5].find((rating: Rating) => !onlyRatings.has(rating));
        if (allSelected) setOnlyRatings(new Set());
        else setOnlyRatings(new Set([1, 2, 3, 4, 5]));
    }

    function onToggleRating(rating: Rating) {
        return function() {
            const selected = new Set(onlyRatings);
            if (selected.has(rating)) selected.delete(rating);
            else selected.add(rating);
            setOnlyRatings(selected);
        }
    }

    function onStartExport() {
        if (!selectedAccounts.size) return;
        setLoading(true);
        const dateFormat = 'YYYY-MM-DD';
        exportRatings({
            data: {
                account_ids: Array.from(selectedAccounts),
                ean: nullify(ean),
                only_ratings: nullify(Array.from(onlyRatings)),
                from_date: nullify(fromDate?.format(dateFormat)),
                to_date: nullify(toDate?.format(dateFormat))
            }, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    onReload();
                    onClose();
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het starten van de export is niet gelukt.'),
                        'Exporteren mislukt');
            }
        })
    }

    useEffect(() => {
        if (account) setSelectedAccounts(new Set([account.id]));
    }, [account]);

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <CreateRatingExport onClose={onClose} onChangeFromDate={onChangeFromDate} onChangeToDate={onChangeToDate}
                            onToggleAllRatings={onToggleAllRatings} onToggleRating={onToggleRating}
                            onStartExport={onStartExport} onSelectAccounts={onSelectAccounts} fromDate={fromDate}
                            toDate={toDate} onlyRatings={onlyRatings} ean={ean} loading={loading} active={active}
                            selectedAccounts={selectedAccounts}/>
    )
}

export default forwardRef<ActivateHandle, Props>(CreateRatingExportController);